/**
 * Telegram Mini App SDK integration utilities.
 */

import { retrieveLaunchParams, type LaunchParams } from '@tma.js/sdk-react'

/**
 * Get Telegram launch parameters.
 */
export function getLaunchParams(): LaunchParams | null {
  try {
    return retrieveLaunchParams()
  } catch {
    return null
  }
}

/**
 * Get Telegram user from launch params.
 */
export function getTelegramUser() {
  const params = getLaunchParams()
  return params?.initData?.user ?? null
}

/**
 * Get raw initData for backend authentication.
 */
export function getInitDataRaw(): string | null {
  const params = getLaunchParams()
  return params?.initDataRaw ?? null
}

/**
 * Check if running inside Telegram.
 */
export function isTelegramMiniApp(): boolean {
  return typeof window !== 'undefined' &&
         window.Telegram?.WebApp !== undefined
}

/**
 * Show Telegram alert.
 */
export function showAlert(message: string): void {
  if (isTelegramMiniApp()) {
    window.Telegram.WebApp.showAlert(message)
  } else {
    alert(message)
  }
}

/**
 * Show Telegram confirm dialog.
 */
export function showConfirm(
  message: string,
  callback: (confirmed: boolean) => void
): void {
  if (isTelegramMiniApp()) {
    window.Telegram.WebApp.showConfirm(message, callback)
  } else {
    const result = confirm(message)
    callback(result)
  }
}

/**
 * Close Mini App.
 */
export function closeMiniApp(): void {
  if (isTelegramMiniApp()) {
    window.Telegram.WebApp.close()
  }
}

/**
 * Expand Mini App to full height.
 */
export function expandMiniApp(): void {
  if (isTelegramMiniApp()) {
    window.Telegram.WebApp.expand()
  }
}

/**
 * Enable haptic feedback.
 */
export const haptic = {
  impact(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'light') {
    if (isTelegramMiniApp()) {
      window.Telegram.WebApp.HapticFeedback.impactOccurred(style)
    }
  },
  notification(type: 'error' | 'success' | 'warning') {
    if (isTelegramMiniApp()) {
      window.Telegram.WebApp.HapticFeedback.notificationOccurred(type)
    }
  },
  selection() {
    if (isTelegramMiniApp()) {
      window.Telegram.WebApp.HapticFeedback.selectionChanged()
    }
  },
}

/**
 * Open link in Telegram browser.
 */
export function openLink(url: string, options?: { tryInstantView?: boolean }): void {
  if (isTelegramMiniApp()) {
    window.Telegram.WebApp.openLink(url, options)
  } else {
    window.open(url, '_blank')
  }
}

/**
 * Open Telegram link (for opening chats, channels, etc).
 */
export function openTelegramLink(url: string): void {
  if (isTelegramMiniApp()) {
    window.Telegram.WebApp.openTelegramLink(url)
  } else {
    window.open(url, '_blank')
  }
}

// Type declarations for Telegram Web App
declare global {
  interface Window {
    Telegram: {
      WebApp: {
        ready: () => void
        expand: () => void
        close: () => void
        showAlert: (message: string, callback?: () => void) => void
        showConfirm: (message: string, callback: (confirmed: boolean) => void) => void
        openLink: (url: string, options?: { tryInstantView?: boolean }) => void
        openTelegramLink: (url: string) => void
        HapticFeedback: {
          impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void
          notificationOccurred: (type: 'error' | 'success' | 'warning') => void
          selectionChanged: () => void
        }
        initData: string
        initDataUnsafe: {
          user?: {
            id: number
            first_name: string
            last_name?: string
            username?: string
            language_code?: string
          }
          start_param?: string
        }
        themeParams: Record<string, string>
        colorScheme: 'light' | 'dark'
        viewportHeight: number
        viewportStableHeight: number
        isExpanded: boolean
        MainButton: {
          text: string
          color: string
          textColor: string
          isVisible: boolean
          isActive: boolean
          isProgressVisible: boolean
          show: () => void
          hide: () => void
          enable: () => void
          disable: () => void
          showProgress: (leaveActive?: boolean) => void
          hideProgress: () => void
          setText: (text: string) => void
          onClick: (callback: () => void) => void
          offClick: (callback: () => void) => void
        }
        BackButton: {
          isVisible: boolean
          show: () => void
          hide: () => void
          onClick: (callback: () => void) => void
          offClick: (callback: () => void) => void
        }
      }
    }
  }
}
